"use client";

import { Download, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import DistrictCard from "@/app/review/components/DistrictCard";
import ProgressBar from "@/app/review/components/ProgressBar";
import ReviewControls from "@/app/review/components/ReviewControls";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { exportReviewResults } from "@/lib/csvParser";
import {
  clearSession,
  createNewSession,
  createReviewResult,
  getSessionStats,
  loadSession,
  saveSession,
  updateSessionWithReview,
} from "@/lib/reviewState";
import {
  DistrictData,
  ReviewAction,
  ReviewSession,
  ReviewState,
} from "@/types/review";

export default function ReviewPage() {
  const [state, setState] = useState<ReviewState>({
    districts: [],
    currentIndex: 0,
    session: createNewSession(0),
    isLoading: true,
    error: null,
  });

  const [notes, setNotes] = useState("");
  const [selectedUrl, setSelectedUrl] = useState("");

  // Load initial data
  useEffect(() => {
    const initializeData = () => {
      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        // Load CSV data from sessionStorage
        const csvDataString = sessionStorage.getItem("csvData");
        if (!csvDataString) {
          throw new Error("No CSV data found. Please upload a CSV file.");
        }

        const districts: DistrictData[] = JSON.parse(
          csvDataString,
        ) as DistrictData[];

        // Try to load existing session
        const savedSession = loadSession();
        let session: ReviewSession;

        if (savedSession && savedSession.total_districts === districts.length) {
          session = savedSession;
        } else {
          session = createNewSession(districts.length);
        }

        setState({
          districts,
          currentIndex: session.current_index,
          session,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : "Failed to load data",
        }));
      }
    };

    initializeData();
  }, []);

  // Set initial selected URL when district changes
  useEffect(() => {
    if (
      state.districts.length > 0 &&
      state.currentIndex < state.districts.length
    ) {
      const currentDistrict = state.districts[state.currentIndex];
      if (currentDistrict) {
        const primaryUrl = currentDistrict.current_ats_url;
        if (primaryUrl && primaryUrl.trim() !== "") {
          setSelectedUrl(primaryUrl);
        } else if (
          currentDistrict.ats_url_1 &&
          currentDistrict.ats_url_1.trim() !== ""
        ) {
          setSelectedUrl(currentDistrict.ats_url_1);
        } else if (
          currentDistrict.ats_url_2 &&
          currentDistrict.ats_url_2.trim() !== ""
        ) {
          setSelectedUrl(currentDistrict.ats_url_2);
        } else {
          setSelectedUrl("");
        }
        setNotes(""); // Reset notes for new district
      }
    }
  }, [state.currentIndex, state.districts]);

  const handleReviewAction = useCallback(
    (action: ReviewAction) => {
      if (state.currentIndex >= state.districts.length) return;

      const currentDistrict = state.districts[state.currentIndex];
      if (!currentDistrict) return;

      const reviewedUrl = selectedUrl || currentDistrict.current_ats_url || "";

      const reviewResult = createReviewResult(
        currentDistrict,
        action,
        notes,
        state.session.session_id,
        reviewedUrl,
      );

      const updatedSession = updateSessionWithReview(
        state.session,
        reviewResult,
        true,
      );

      setState((prev) => ({
        ...prev,
        session: updatedSession,
        currentIndex: updatedSession.current_index,
      }));

      // Save to localStorage
      saveSession(updatedSession);

      // Reset notes for next district
      setNotes("");
    },
    [state.currentIndex, state.districts, state.session, selectedUrl, notes],
  );

  const handleExport = useCallback(() => {
    const reviewedItems = state.session.reviews.filter(
      (r) => r.review_status !== "unreviewed",
    );
    if (reviewedItems.length === 0) {
      // eslint-disable-next-line no-alert
      window.alert("No reviews to export yet!");
      return;
    }

    const csvContent = exportReviewResults(reviewedItems);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `url_review_results_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [state.session.reviews]);

  const handleNewSession = useCallback(() => {
    if (
      // eslint-disable-next-line no-alert
      window.confirm("This will clear your current progress. Are you sure?")
    ) {
      clearSession();
      const newSession = createNewSession(state.districts.length);
      setState((prev) => ({
        ...prev,
        session: newSession,
        currentIndex: 0,
      }));
      setNotes("");
    }
  }, [state.districts.length]);

  const navigateToDistrict = useCallback(
    (index: number) => {
      if (index >= 0 && index < state.districts.length) {
        const updatedSession = { ...state.session, current_index: index };
        setState((prev) => ({
          ...prev,
          currentIndex: index,
          session: updatedSession,
        }));
        saveSession(updatedSession);
      }
    },
    [state.districts.length, state.session],
  );

  // Handle keyboard shortcuts at page level
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input/textarea or contenteditable
      const target = e.target as HTMLElement;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.contentEditable === "true" ||
        target.isContentEditable;

      // Also check if any modifier keys are pressed (Ctrl, Alt, Meta)
      // to avoid conflicts with browser shortcuts
      if (isTyping || e.ctrlKey || e.altKey || e.metaKey) {
        return;
      }

      // Handle review action shortcuts
      switch (e.key.toLowerCase()) {
        case "c":
        case "1":
          e.preventDefault();
          handleReviewAction("correct");
          break;
        case "i":
        case "2":
          e.preventDefault();
          handleReviewAction("incorrect");
          break;
        case "s":
        case "3":
          e.preventDefault();
          handleReviewAction("skip");
          break;
        case "arrowleft":
          e.preventDefault();
          navigateToDistrict(state.currentIndex - 1);
          break;
        case "arrowright":
          e.preventDefault();
          navigateToDistrict(state.currentIndex + 1);
          break;
        case "n": {
          // Focus the notes field
          e.preventDefault();
          const notesField = document.getElementById("review-notes");
          if (notesField) {
            notesField.focus();
          }
          break;
        }
        case "escape":
          // Clear focus from any element
          e.preventDefault();
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [state.currentIndex, navigateToDistrict, handleReviewAction]);

  if (state.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span>Loading uploaded data...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 max-w-md">
          <CardContent>
            <div className="text-center space-y-4">
              <h2 className="text-xl font-bold text-red-600">
                Error Loading Data
              </h2>
              <p className="text-gray-600">{state.error}</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => (window.location.href = "/")}>
                  Upload CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state.districts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8">
          <CardContent>
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-600">No Data Found</h2>
              <p className="text-gray-500 mt-2">
                Could not find district data to review.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentDistrict = state.districts[state.currentIndex];
  const stats = getSessionStats(state.session);
  const isComplete = state.currentIndex >= state.districts.length;

  // Early return if no current district and not complete
  if (!currentDistrict && !isComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8">
          <CardContent>
            <div className="text-center space-y-4">
              <h2 className="text-xl font-bold text-gray-600">
                Invalid District Index
              </h2>
              <p className="text-gray-500 mt-2">
                Unable to find district data for current position.
              </p>
              <Button onClick={() => (window.location.href = "/")}>
                Upload New CSV
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 text-center">
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-green-600">
                    🎉 Review Complete!
                  </h1>
                  <p className="text-gray-600 mt-2">
                    You&apos;ve reviewed all {state.districts.length} districts
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 py-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      {stats.totalReviewed}
                    </div>
                    <div className="text-sm text-gray-500">Total Reviewed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {stats.correctCount}
                    </div>
                    <div className="text-sm text-gray-500">Correct</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">
                      {stats.incorrectCount}
                    </div>
                    <div className="text-sm text-gray-500">Incorrect</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-600">
                      {stats.skippedCount}
                    </div>
                    <div className="text-sm text-gray-500">Skipped</div>
                  </div>
                </div>

                <div className="flex gap-4 justify-center">
                  <Button
                    onClick={handleExport}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export Results
                  </Button>
                  <Button
                    onClick={handleNewSession}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Start New Session
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              URL Review Tool
            </h1>
            <p className="text-gray-600">
              Review school district job board URLs for accuracy
            </p>
          </div>
          <div className="flex gap-3 items-center">
            <div className="text-sm text-gray-500">
              Shortcuts:{" "}
              <kbd className="px-1.5 py-0.5 text-xs bg-gray-100 rounded border">
                C
              </kbd>{" "}
              Correct,{" "}
              <kbd className="px-1.5 py-0.5 text-xs bg-gray-100 rounded border">
                I
              </kbd>{" "}
              Incorrect,{" "}
              <kbd className="px-1.5 py-0.5 text-xs bg-gray-100 rounded border">
                S
              </kbd>{" "}
              Skip,{" "}
              <kbd className="px-1.5 py-0.5 text-xs bg-gray-100 rounded border">
                N
              </kbd>{" "}
              Notes
            </div>
            <Button
              onClick={handleExport}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export ({stats.totalReviewed})
            </Button>
            <Button
              onClick={handleNewSession}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              New Session
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <ProgressBar
          current={state.currentIndex}
          total={state.districts.length}
          reviewedCount={stats.totalReviewed}
          correctCount={stats.correctCount}
          incorrectCount={stats.incorrectCount}
          skippedCount={stats.skippedCount}
          timeElapsed={stats.timeElapsed}
          reviewsPerHour={stats.reviewsPerHour}
        />

        {/* Main Content - Split Screen Layout */}
        <div className="flex gap-6 h-[calc(100vh-280px)]">
          {/* Left Panel - Controls and District Info (50%) */}
          <div className="w-1/2 space-y-6 overflow-y-auto">
            {currentDistrict && (
              <DistrictCard
                district={currentDistrict}
                selectedUrl={selectedUrl}
                onUrlSelect={setSelectedUrl}
                showPreview={false}
              />
            )}
            <ReviewControls
              onAction={handleReviewAction}
              notes={notes}
              onNotesChange={setNotes}
              currentUrl={selectedUrl}
              isLoading={state.isLoading}
            />
          </div>

          {/* Right Panel - Large Preview (50%) */}
          <div className="w-1/2">
            <div className="h-full border rounded-lg overflow-hidden bg-gray-50">
              {selectedUrl ? (
                <iframe
                  src={selectedUrl}
                  className="w-full h-full"
                  title={`Preview of ${currentDistrict?.district_name || "District"} job board`}
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <div className="text-center space-y-2">
                    <div className="text-lg font-medium">No URL Selected</div>
                    <div className="text-sm">
                      Choose a URL from the left panel to preview
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-6 pt-6 border-t">
          <Button
            onClick={() => navigateToDistrict(state.currentIndex - 1)}
            disabled={state.currentIndex === 0}
            variant="outline"
          >
            ← Previous
          </Button>

          <span className="text-sm text-gray-500">
            Use ← → arrow keys to navigate
          </span>

          <Button
            onClick={() => navigateToDistrict(state.currentIndex + 1)}
            disabled={state.currentIndex >= state.districts.length - 1}
            variant="outline"
          >
            Next →
          </Button>
        </div>
      </div>
    </div>
  );
}
